import { gql } from '@apollo/client';

export const GET_CLIENT_FOR_COACH = gql`
  query GetClientsForCoach(
    $coachId: String!
    $take: Int!
    $skip: Int!
    $nameText: String
    $isArchived: Boolean!
    $orderBy: String
    $order: String
    $clientType: ClientType
    $clientGroupId: String
  ) {
    getClientsByCoachId(
      coachId: $coachId
      take: $take
      skip: $skip
      nameText: $nameText
      isArchived: $isArchived
      orderBy: $orderBy
      order: $order
      clientType: $clientType
      clientGroupId: $clientGroupId
    ) {
      totalClientCount
      clients {
        client {
          id
          firstName
          lastName
          profilePic
          surname
          email
          objectives {
            objectiveName
            colorCode
          }
          isActive
          updatedAt
          teamMembers {
            id
            firstName
            lastName
            profilePic
            email
            surname
            timeZone
            language
            pronoun
          }
          clientType
        }
        engagementLevel {
          clientId
          engagementLevels {
            date
            engagementLevel
          }
        }
      }
      clientCountStatus
      paymentStatus
      subscriptionPlanStatus {
        currentPlan
        daysLeft
        paymentStatus
        nextPlan
      }
    }
  }
`;

export const GET_CLIENT_OBJECTIVES = gql`
  query GetClientById($id: String!) {
    getClientById(id: $id) {
      objectives {
        objectiveName
        colorCode
      }
      clientType
    }
  }
`;

export const GET_CLIENT_BY_ID = gql`
  query GetClientById($id: String!) {
    getClientById(id: $id) {
      id
      firstName
      lastName
      description
      surname
      email
      profilePic
      dob
      gender
      tp
      addressLine1
      addressLine2
      city
      state
      pronoun
      createdAt
      updatedAt
      organizationId
      archived
      isActive
      objectives {
        objectiveName
        colorCode
      }
      clientType
      teamMembers {
        id
        firstName
        lastName
        profilePic
        email
        surname
        timeZone
        language
        pronoun
      }
      totalUnreadMessages
      coach {
        id
        firstName
        lastName
        profilePic
        email
        surname
        timeZone
        language
        pronoun
      }
    }
  }
`;

export const CREATE_CLIENT_MUTATION = gql`
  mutation CreateClient($CreateClient: CreateClient!) {
    createClient(CreateClient: $CreateClient) {
      id
      firstName
      lastName
      surname
      email
      profilePic
      dob
      gender
      tp
      addressLine1
      addressLine2
      city
      state
      pronoun
      createdAt
      updatedAt
      organizationId
      archived
      objectives {
        objectiveName
        colorCode
      }
    }
  }
`;

export const CREATE_CLIENT_GROUP_MUTATION = gql`
  mutation CreateClientGroup($createClientGroupInput: AddClientsToCoachGroupInput!) {
    createClientGroup(createClientGroupInput: $createClientGroupInput) {
      success
      message
      statusCode
    }
  }
`;

export const UPDATE_CLIENT_OBJECTIVES_MUTATION = gql`
  mutation UpdateClientObjectives($UpdateClientObjectives: UpdateClientObjectives!) {
    updateClientObjectives(UpdateClientObjectives: $UpdateClientObjectives) {
      message
      success
      statusCode
    }
  }
`;

export const UPDATE_CLIENT_COACH_ASSIGNMENT = gql`
  mutation UpdateCoachAssignments($clientId: String!, $coachIds: [String!]!) {
    updateCoachAssignments(clientId: $clientId, coachIds: $coachIds) {
      message
      success
      statusCode
    }
  }
`;

export const ARCHIVE_CLIENT_MUTATION = gql`
  mutation DeleteClient($clientId: String!, $coachId: String!, $email: String!, $password: String) {
    deleteClient(clientId: $clientId, coachId: $coachId, email: $email, password: $password) {
      id
      firstName
      lastName
      surname
      email
    }
  }
`;

export const GET_ACTIVITIES_COMPLETED_BY_CLIENT = gql`
  query GetActivitiesCompletedByClient(
    $coachId: String!
    $from: DateTime!
    $to: DateTime!
    $skip: Int
    $take: Int
  ) {
    getClientCompletedActivities(
      coachId: $coachId
      from: $from
      to: $to
      skip: $skip
      take: $take
    ) {
      completedActivities {
        firstName
        lastName
        clientId
        coachId
        activityName
        completedDate
        clientProfilePic
      }
      totalCompletedActivityCount
    }
  }
`;

export const SUBSCRIBE_TO_CLIENT_ACTIVITIES = gql`
  subscription SubscribeToClientActivities($coachId: String!) {
    clientActivities(coachId: $coachId) {
      activityName
      firstName
      lastName
      clientId
      coachId
      completedDate
    }
  }
`;

export const GET_CLIENT_OBJECTIVES_PROGRESS_DATA = gql`
  query GetClientObjectivesProgressData(
    $fromDate: DateTime!
    $toDate: DateTime!
    $clientId: String!
  ) {
    getObjectiveProgress(fromDate: $fromDate, toDate: $toDate, clientId: $clientId) {
      objectiveId
      objectiveName
      colorCode
      progressList {
        createdAt
        scale
      }
    }
  }
`;

export const GET_USER_ACTIVITY_PROGRESS = gql`
  query GetProgressSummary($clientId: String!, $coachId: String!) {
    progressSummary(clientId: $clientId, coachId: $coachId) {
      engagementLevels {
        date
        engagementLevel
      }
      objectiveProgressData {
        objectiveId
        objectiveName
        colorCode
        progressList {
          createdAt
          scale
        }
      }
      suggestions {
        progress
      }
    }
  }
`;

export const CLIENT_ACTIVITY_SUMMARY_GENERATION = gql`
  query ActivityStatus($coachId: String!, $timeZone: String!) {
    activityStatus(activityStatus: { coachId: $coachId, contentType: "TO", timeZone: $timeZone }) {
      completedStatus {
        name
        activities
      }
      inCompleteActivities {
        name
        activities
      }
    }
  }
`;

export const GET_CLIENT_ACTIVITY_PROGRESS_SUMMARY = gql`
  query GetClientActivityProgressSummary(
    $coachId: String!
    $clientId: String!
    $skip: Int!
    $take: Int!
  ) {
    clientActivityProgressSummary(
      coachId: $coachId
      clientId: $clientId
      skip: $skip
      take: $take
    ) {
      count
      activitySummary {
        name
        schedule
        groupId
        responseSummary {
          advanceStepType
          text
          data {
            createdAt
            value
            type
            duration
          }
        }
        activityDetails {
          id
          completedAt
          isAdvanceActivity
          ActivityStep {
            id
            text
            isAdvanceStep
            advanceStepType
            stepOrder
            ActivityStepResponse {
              id
              asset
              assetType
              assetDuration
            }
          }
        }
      }
    }
  }
`;

export const UPDATE_CLIENT_STATUS_MUTATION = gql`
  mutation ($clientId: String!, $status: ClientStatus!) {
    changeClientStatus(UpdateStatus: { clientId: $clientId, status: $status }) {
      id
      isActive
    }
  }
`;

export const CREATE_CLIENT_CONSENT = gql`
  mutation CreateClientConcent(
    $coachId: String!
    $clientId: String!
    $noteAssistEnabled: Boolean!
    $magicSuggestionsEnabled: Boolean!
  ) {
    createClientConcent(
      createConcent: {
        coachId: $coachId
        clientId: $clientId
        noteAssistEnabled: $noteAssistEnabled
        magicSuggestionsEnabled: $magicSuggestionsEnabled
      }
    ) {
      id
      noteAssistEnabled
      magicSuggestionsEnabled
      createdAt
    }
  }
`;

export const GET_CLIENT_CONSENT = gql`
  query GetClientConcent($coachId: String!, $clientId: String!) {
    getClientConcent(coachId: $coachId, clientId: $clientId) {
      id
      clientId
      coachId
      noteAssistEnabled
      magicSuggestionsEnabled
      createdAt
      isArchived
      updatedAt
    }
  }
`;

export const UPDATE_CLIENT_CONSENT = gql`
  mutation UpdateClientConcent(
    $concentId: String!
    $coachId: String!
    $clientId: String!
    $noteAssistEnabled: Boolean!
    $magicSuggestionsEnabled: Boolean!
  ) {
    updateClientConcent(
      updateConcent: {
        concentId: $concentId
        coachId: $coachId
        clientId: $clientId
        noteAssistEnabled: $noteAssistEnabled
        magicSuggestionsEnabled: $magicSuggestionsEnabled
      }
    ) {
      id
      clientId
      coachId
      noteAssistEnabled
      magicSuggestionsEnabled
      createdAt
      isArchived
      updatedAt
    }
  }
`;

export const RESEND_CLIENT_INVITATION = gql`
  mutation resendClientInvitation($clientId: String!) {
    resendClientInvitation(clientId: $clientId) {
      success
      message
    }
  }
`;

export const ADD_CLIENT_GROUP = gql`
  mutation createClientGroup($createClientGroupInput: createClientGroupInput!) {
    createClientGroup(createClientGroup: $createClientGroup) {
      success
      message
      statusCode
    }
  }
`;

export default GET_CLIENT_FOR_COACH;
